import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Divider, Stack, styled, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import AttachmentBox from '../../../components/AttachmentBox/AttachmentBox';
import DetailsCard from '../../../components/DetailsCard/DetailsCard';
import FormatCurrency from '../../../components/FormatNumber/FormatCurrency';
import PageTitle from '../../../components/PageTitle/PageTitle';
import TypographyDash from '../../../components/TypographyDash/TypographyDash';
import { InvoiceStatus } from '../../../enums/invoiceStatus';
import { FileType } from '../../../models/fileType';
import { Invoice } from '../../../models/invoice';
import { INVOICES, PROJECTS } from '../../../router/constants';
import { fileService } from '../../../services/fileService';
import { invoiceService } from '../../../services/invoiceService';
import { projectService } from '../../../services/projectService';
import calculateInvoiceTotal from '../../../utils/invoiceTotal';
import InvoiceForm from '../InvoiceForm';
import { managerService } from '../../../services/managerService';
import { Manager } from '../../../models/manager';

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900']
}));

const schema = yup.object({
  projectTitle: yup.string().default('').required(),
  companyName: yup.string().default('').required(),
  talentFullName: yup.string().default('').required(),
  manager: yup.object().default(null).required(),
  description: yup.string().default('').nullable(),
  address: yup.string().default('').nullable(),
  category: yup.string().default(''),
  expenseCode: yup.string().default(''),
  expenses: yup.number().default(null).nullable(),
  initials: yup.string().default('').nullable(),
  talentNotes: yup.string().default('').nullable(),
  id: yup.number().default(0),
  status: yup.string().default(''),
  createdOn: yup.string().default(''),
  total: yup
    .number()
    .default(null)
    .test('has hourlyRate and totalHours', '', (value, context) => {
      return !(
        (!context.parent.hourlyRate || !context.parent.totalHours) &&
        !value
      );
    })
    .nullable(),
  hourlyRate: yup
    .number()
    .default(null)
    .test('has totalHours', '', (value, context) => {
      return !(!value && context.parent.totalHours);
    })
    .nullable(),
  totalHours: yup
    .number()
    .default(null)
    .test('has hourlyRate', '', (value, context) => {
      return !(!value && context.parent.hourlyRate);
    })
    .nullable()
});

type EdtiInvoiceForm = yup.InferType<typeof schema>;

const defaultValues = schema.getDefault();

const InvoiceDetails = (): JSX.Element => {
  const navigate = useNavigate();
  const params = useParams();
  const [invoice, setInvoice] = useState<Invoice | undefined>(undefined);
  const [isFlatFee, setIsFlatFee] = useState(false);
  const [managerList, setManagerList] = useState<Manager[] | undefined>();
  const [isProjectInvoice, setIsProjectInvoice] = useState<boolean>(false);
  const [edit, setEdit] = useState(false);
  const [invoiceFiles, setInvoiceFiles] = useState<FileType[]>();
  const [invoiceFilesToDelete, setInvoiceFilesToDelete] =
    useState<FileType[]>();

  const [currentTotal, setCurrentTotal] = useState<number>(0);

  const methods = useForm<EdtiInvoiceForm>({
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onBlur'
  });

  const { setValue, handleSubmit } = methods;

  useEffect(() => {
    if (params.id) {
      getInvoice();
    }
  }, []);

  useEffect(() => {
    if (invoice) {
      setValue('projectTitle', invoice.projectTitle);
      setValue('companyName', invoice.companyName ?? '');
      setValue('manager', invoice.manager);
      setValue('talentFullName', invoice.talentFullName);
      setValue('expenses', invoice.expenses);
      setValue('category', invoice.category);
      setValue('expenseCode', invoice.expenseCode);
      setValue('address', invoice.address ?? '');
      if (invoice.hourlyRate && invoice.totalHours) {
        setValue('hourlyRate', invoice?.hourlyRate);
        setValue('totalHours', invoice?.totalHours);
      } else {
        setValue('total', invoice.total);
      }
      setCurrentTotal(calculateInvoiceTotal(invoice));
      setValue('description', invoice.description ?? '');
      setValue('talentNotes', invoice.talentNotes ?? '');
      setValue('id', invoice.id ?? '');
      setValue('status', invoice.status ?? '');
      setValue('createdOn', invoice.createdOn ?? '');
      setInvoiceFiles(invoice.attachments);
    }
  }, [invoice]);

  const handleCreateInvoice = (
    data: EdtiInvoiceForm,
    files: File[],
    isDraft = false
  ) => {
    const output = {
      ...data,
      projectId: invoice?.id,
      hourlyRate: data?.hourlyRate,
      totalHours: data?.totalHours
    } as Invoice;

    invoiceService
      .updateInvoice({ data: output, submit: !isDraft })
      .then((response) => {
        navigate(INVOICES);
        if (files.length && response?.data?.id) {
          const formDataFiles = new FormData();
          files.forEach((file) => {
            formDataFiles.append('files', file);
          });

          invoiceService
            .saveProjectFiles(response.data.id, formDataFiles)
            .then(() => navigate(INVOICES));
        }
        if (invoiceFilesToDelete?.length) {
          invoiceFilesToDelete.forEach((file) => {
            fileService.deleteFileById(file.uuid);
          });
        }
      })
      .finally(() => {
        if (params?.closeProject === 'close' && invoice?.projectId) {
          projectService.closeProjectById(invoice.projectId);
        }
      });
  };

  const getInvoice = () => {
    if (params.id) {
      invoiceService.getTalentInvoiceById(+params.id).then((response) => {
        setInvoice(response.data);
        if (response.data.projectId) {
          setIsProjectInvoice(true);
        } else {
          getManagerList();
        }
      });
    }
  };

  const getManagerList = () => {
    managerService.getManagerList().then((response) => {
      setManagerList(response.data);
    });
  };

  const downloadAttachment = (uuid: string) => {
    fileService.getFileUrl(`${uuid}`).then((response) => {
      window.open(response.data, 'blank');
    });
  };

  useEffect(() => {
    if (!invoice?.totalHours && !invoice?.hourlyRate && invoice?.total) {
      setIsFlatFee(true);
    }
  }, [invoice]);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '1200px'
        }}
      >
        <PageTitle>Invoice Details</PageTitle>
      </div>
      {edit && (
        <FormProvider {...methods}>
          <form>
            <InvoiceForm
              title={`Edit Invoice #${params.id}`}
              isEditing={edit}
              setIsEditing={setEdit}
              invoiceFiles={invoiceFiles}
              projectAttachments={invoice?.projectAttachments}
              managerList={managerList}
              isProjectInvoice={isProjectInvoice}
              setInvoiceFiles={setInvoiceFiles}
              setInvoiceFilesToDelete={setInvoiceFilesToDelete}
              handleSubmit={(files: File[], isDraft?: boolean) =>
                handleSubmit((data) =>
                  handleCreateInvoice(data, files, isDraft)
                )()
              }
            />
          </form>
        </FormProvider>
      )}
      {!edit && (
        <DetailsCard
          title={`Invoice #${params.id}`}
          subtitle={
            invoice
              ? `Submitted on: ${format(
                  new Date(invoice.createdOn),
                  'MMM do, Y'
                )}`
              : ''
          }
          status={invoice ? InvoiceStatus[invoice?.status] : undefined}
        >
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Box
              sx={{
                p: 3,
                borderRadius: '10px',
                border: '1px dashed #B3B3B3',
                display: 'flex',
                gap: 3,
                flexDirection: { xs: 'column', lg: 'row' }
              }}
            >
              <Box sx={{ flex: '1 1 0px' }}>
                <Typography variant="h6" color="grey.900" mb={2}>
                  PO #{invoice?.poNumber ?? '--'}
                </Typography>

                <Box display="flex" gap={3}>
                  <Stack spacing={1} sx={{ flexShrink: 0 }}>
                    <BoldText>Project Title:</BoldText>
                    <BoldText>Company:</BoldText>
                    <BoldText>Manager:</BoldText>
                    <BoldText>Talent:</BoldText>
                    <BoldText>Category:</BoldText>
                    <BoldText>Expense Code:</BoldText>
                    <BoldText>Location:</BoldText>
                    <BoldText>Description:</BoldText>
                  </Stack>
                  <Stack spacing={1} sx={{ flex: '1 0 160px' }}>
                    {invoice?.projectId ? (
                      <Typography
                        sx={{ color: 'primary.main', cursor: 'pointer' }}
                        onClick={() =>
                          navigate(`${PROJECTS}/${invoice?.projectId}`)
                        }
                      >
                        {invoice?.projectTitle ?? '--'}
                      </Typography>
                    ) : (
                      <TypographyDash value={invoice?.projectTitle} />
                    )}
                    <TypographyDash value={invoice?.companyName} />
                    <TypographyDash value={invoice?.manager?.fullName} />
                    <TypographyDash value={invoice?.talentFullName} />
                    <TypographyDash value={invoice?.category} />
                    <TypographyDash value={invoice?.expenseCode} />
                    <TypographyDash value={invoice?.address} />
                    <TypographyDash value={invoice?.description} />
                  </Stack>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: '1 1 0px',
                  alignItems: { xs: 'start', lg: 'end' }
                }}
              >
                <Box mb={3}>
                  <Typography mb={1}>Project Attachments:</Typography>
                  {invoice?.projectAttachments?.map((file) => (
                    <AttachmentBox
                      key={file.id}
                      onClickFile={() => downloadAttachment(file.uuid)}
                      fileName={file.fileName}
                    />
                  ))}
                  {!invoice?.projectAttachments?.length && (
                    <AttachmentBox fileName="No Attachments" />
                  )}
                </Box>
                <Box>
                  <Typography mb={1}>Talent Attachments:</Typography>
                  {invoice?.attachments?.map((file) => (
                    <AttachmentBox
                      key={file.id}
                      onClickFile={() => downloadAttachment(file.uuid)}
                      fileName={file.fileName}
                    />
                  ))}
                  {(!invoice || !invoice.attachments?.length) && (
                    <AttachmentBox fileName="No Attachments" />
                  )}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                gap: 2,
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' }
              }}
            >
              <Box
                sx={{
                  p: 3,
                  borderRadius: '10px',
                  border: '1px dashed #B3B3B3',
                  flex: '1 1 0px'
                }}
              >
                <BoldText>Talent Notes:</BoldText>
                {invoice?.talentNotes}
              </Box>

              <Box
                sx={{
                  p: 3,
                  borderRadius: '10px',
                  border: '1px dashed #B3B3B3',
                  flex: '1 1 0px',
                  bgcolor: '#F6F7FB'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { sx: 'start', lg: 'end' },
                    gap: 2
                  }}
                >
                  {!isFlatFee && (
                    <>
                      <Box
                        sx={{
                          textAlign: { sx: 'start', lg: 'end' },
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-around'
                        }}
                      >
                        <Typography>Total Hours Worked:</Typography>
                        <Typography>Pay/Hour:</Typography>
                        <Typography>Expenses:</Typography>
                      </Box>
                      <Box textAlign="right">
                        <>
                          <BoldText
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'end'
                            }}
                          >
                            {invoice?.totalHours
                              ? `${invoice?.totalHours}h`
                              : '0'}
                          </BoldText>
                          <BoldText
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'end'
                            }}
                          >
                            <FormatCurrency value={invoice?.hourlyRate ?? 0} />
                          </BoldText>
                          <BoldText
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'end'
                            }}
                          >
                            <FormatCurrency value={invoice?.expenses ?? 0} />
                          </BoldText>
                        </>
                      </Box>
                    </>
                  )}
                  {isFlatFee && (
                    <Box width="100%">
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: { sx: 'start', lg: 'end' },
                          gap: 2
                        }}
                      >
                        <Box
                          sx={{
                            textAlign: { sx: 'start', lg: 'end' },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-around'
                          }}
                        >
                          <Typography whiteSpace="nowrap">
                            Invoice Fee Total:
                          </Typography>
                          <Typography whiteSpace="nowrap">Expenses:</Typography>
                        </Box>
                        <>
                          <Box
                            textAlign="right"
                            sx={{
                              display: 'flex',
                              gap: 2,
                              flexDirection: 'column'
                            }}
                          >
                            <BoldText
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'end'
                              }}
                            >
                              <FormatCurrency value={invoice?.total ?? 0} />
                            </BoldText>
                            <BoldText
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'end'
                              }}
                            >
                              <FormatCurrency value={invoice?.expenses ?? 0} />
                            </BoldText>
                          </Box>
                        </>
                      </Box>
                    </Box>
                  )}
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { sx: 'start', lg: 'end' },
                    gap: 2,
                    alignItems: 'center'
                  }}
                >
                  <Typography>Invoice Total:</Typography>
                  <Typography variant="h5" color="grey.900" fontWeight="bold">
                    <FormatCurrency value={currentTotal ?? 0} />
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              spacing={2}
              mt={1}
            >
              {!edit && invoice?.status !== InvoiceStatus.APPROVED && (
                <Button sx={{ width: '180px' }} onClick={() => setEdit(true)}>
                  Edit Invoice
                </Button>
              )}
            </Stack>
          </Box>
        </DetailsCard>
      )}
    </>
  );
};

export default InvoiceDetails;
